import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Users, TrendingUp, TrendingDown, Wallet, Building2, UserCog, BarChart3,
  ArrowUpRight, ArrowDownRight, Clock, FileText, ShieldCheck, MessageSquare,
  Activity, Filter, DollarSign, Target, Percent
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminStats() {
  const token = localStorage.getItem('token');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchStats = async (sd, ed) => {
    setLoading(true);
    try {
      let url = `${API}/stats/global`;
      const params = [];
      if (sd) params.push(`start_date=${sd}`);
      if (ed) params.push(`end_date=${ed}`);
      if (params.length) url += '?' + params.join('&');
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch { toast.error('Erreur chargement statistiques'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStats(); }, []);

  const fmt = (n) => (n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtN = (n) => (n || 0).toLocaleString('fr-FR');

  if (loading || !stats) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  const isSuperAdmin = stats.scope === 'super_admin';
  const isBureau = stats.scope === 'bureau';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-stats-page">
        {/* Header + Date Filter */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold">Statistiques{isBureau ? ' — Mon Bureau' : ''}</h1>
            <p className="text-sm text-muted-foreground">Vue d'ensemble de la plateforme</p>
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-8 w-36 text-sm" />
            <span className="text-muted-foreground">→</span>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-8 w-36 text-sm" />
            <Button size="sm" className="h-8" onClick={() => fetchStats(startDate, endDate)}>
              <Filter className="w-3.5 h-3.5 mr-1" /> Filtrer
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => { setStartDate(''); setEndDate(''); fetchStats(); }}>
              Tout
            </Button>
          </div>
        </div>

        {/* Section 1: Key Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { icon: Users, label: 'Clients', value: fmtN(stats.users.total_clients), sub: `${fmtN(stats.users.active_clients)} actifs`, color: 'text-blue-500' },
            { icon: Wallet, label: 'AUM Total', value: `${fmt(stats.financials.total_aum)} €`, color: 'text-purple-500' },
            { icon: ArrowUpRight, label: 'Dépôts', value: `${fmt(stats.financials.total_deposits)} €`, color: 'text-green-500' },
            { icon: ArrowDownRight, label: 'Retraits', value: `${fmt(stats.financials.total_withdrawals)} €`, color: 'text-red-500' },
            { icon: TrendingUp, label: 'P&L Trading', value: `${fmt(stats.trading.total_pnl)} €`, color: stats.trading.total_pnl >= 0 ? 'text-green-500' : 'text-red-500' },
            { icon: Target, label: 'Win Rate', value: `${stats.trading.win_rate}%`, color: stats.trading.win_rate >= 50 ? 'text-green-500' : 'text-amber-500' },
          ].map(m => (
            <Card key={m.label} className="bg-card/80">
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <m.icon className={`w-4 h-4 ${m.color}`} />
                  <span className="text-[10px] text-muted-foreground uppercase">{m.label}</span>
                </div>
                <p className="text-lg font-bold">{m.value}</p>
                {m.sub && <p className="text-[10px] text-muted-foreground">{m.sub}</p>}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Section 2: Financials + Trading */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Financials */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-500" /> Flux Financiers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3 text-center text-xs">
                <div className="p-3 rounded-lg bg-green-500/10"><p className="text-lg font-bold text-green-500">+{fmt(stats.financials.total_deposits)} €</p><p className="text-muted-foreground">Dépôts</p></div>
                <div className="p-3 rounded-lg bg-red-500/10"><p className="text-lg font-bold text-red-500">-{fmt(stats.financials.total_withdrawals)} €</p><p className="text-muted-foreground">Retraits</p></div>
                <div className="p-3 rounded-lg bg-blue-500/10"><p className="text-lg font-bold text-blue-500">{fmt(stats.financials.net_flow)} €</p><p className="text-muted-foreground">Flux net</p></div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-3 text-center text-xs">
                <div><p className="font-bold">{fmtN(stats.financials.total_transactions)}</p><p className="text-muted-foreground">Total TX</p></div>
                <div><p className="font-bold text-amber-500">{fmtN(stats.financials.tx_pending)}</p><p className="text-muted-foreground">En attente</p></div>
                <div><p className="font-bold text-green-500">{fmtN(stats.financials.tx_completed)}</p><p className="text-muted-foreground">Validées</p></div>
                <div><p className="font-bold text-red-500">{fmtN(stats.financials.tx_rejected)}</p><p className="text-muted-foreground">Refusées</p></div>
              </div>
            </CardContent>
          </Card>

          {/* Trading */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-500" /> Performance Trading</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3 text-center text-xs">
                <div className="p-3 rounded-lg bg-primary/10"><p className="text-lg font-bold">{fmtN(stats.trading.total_positions)}</p><p className="text-muted-foreground">Positions</p></div>
                <div className="p-3 rounded-lg bg-green-500/10"><p className="text-lg font-bold text-green-500">{fmtN(stats.trading.winning)}</p><p className="text-muted-foreground">Gagnantes</p></div>
                <div className="p-3 rounded-lg bg-red-500/10"><p className="text-lg font-bold text-red-500">{fmtN(stats.trading.losing)}</p><p className="text-muted-foreground">Perdantes</p></div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-3 text-center text-xs">
                <div><p className="font-bold text-blue-500">{fmtN(stats.trading.open)}</p><p className="text-muted-foreground">Ouvertes</p></div>
                <div><p className="font-bold text-amber-500">{fmtN(stats.trading.pending)}</p><p className="text-muted-foreground">En attente</p></div>
                <div><p className="font-bold text-green-500">{fmtN(stats.trading.closed)}</p><p className="text-muted-foreground">Fermées</p></div>
                <div><p className="font-bold">{stats.trading.win_rate}%</p><p className="text-muted-foreground">Win Rate</p></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section 3: Placements + Compliance */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Wallet className="w-4 h-4 text-purple-500" /> Placements</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-center text-xs">
              <div className="p-3 rounded-lg bg-purple-500/10"><p className="text-lg font-bold text-purple-500">{fmtN(stats.placements.active_subscriptions)}</p><p className="text-muted-foreground">Actifs</p></div>
              <div className="p-3 rounded-lg bg-purple-500/10"><p className="text-lg font-bold">{fmt(stats.placements.total_invested)} €</p><p className="text-muted-foreground">Investis</p></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-amber-500" /> Conformité</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-center text-xs">
              <div className="p-3 rounded-lg bg-amber-500/10"><p className="text-lg font-bold text-amber-500">{fmtN(stats.compliance.pending_kyc)}</p><p className="text-muted-foreground">KYC en attente</p></div>
              <div className="p-3 rounded-lg bg-blue-500/10"><p className="text-lg font-bold">{fmtN(stats.messages.total)}</p><p className="text-muted-foreground">Messages</p></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-cyan-500" /> Intérêts</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-center text-xs">
              <div className="p-3 rounded-lg bg-cyan-500/10"><p className="text-lg font-bold text-cyan-500">{fmt(stats.financials.total_interests_paid)} €</p><p className="text-muted-foreground">Versés</p></div>
              <div className="p-3 rounded-lg bg-green-500/10"><p className="text-lg font-bold">{fmt(stats.financials.total_bonus)} €</p><p className="text-muted-foreground">Bonus</p></div>
            </CardContent>
          </Card>
        </div>

        {/* Section 4: Bureaux Table */}
        {stats.offices.details.length > 0 && !isBureau && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Building2 className="w-4 h-4" /> Rapport par Bureau ({stats.offices.total})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-2">Bureau</th>
                    <th className="text-left py-2 px-2">Admin</th>
                    <th className="text-right py-2 px-2">Clients</th>
                    <th className="text-right py-2 px-2">Analystes</th>
                    <th className="text-right py-2 px-2">AUM</th>
                    <th className="text-right py-2 px-2">Dépôts</th>
                    <th className="text-right py-2 px-2">Retraits</th>
                    <th className="text-right py-2 px-2">P&L</th>
                  </tr></thead>
                  <tbody>
                    {stats.offices.details.map((o, i) => (
                      <tr key={i} className="border-b border-border/30 hover:bg-secondary/30">
                        <td className="py-2 px-2 font-semibold">{o.name}</td>
                        <td className="py-2 px-2 text-muted-foreground">{o.admin}</td>
                        <td className="py-2 px-2 text-right">{o.clients}</td>
                        <td className="py-2 px-2 text-right">{o.analysts}</td>
                        <td className="py-2 px-2 text-right font-mono">{fmt(o.aum)} €</td>
                        <td className="py-2 px-2 text-right font-mono text-green-500">+{fmt(o.deposits)} €</td>
                        <td className="py-2 px-2 text-right font-mono text-red-500">-{fmt(o.withdrawals)} €</td>
                        <td className={`py-2 px-2 text-right font-mono ${o.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>{fmt(o.pnl)} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 5: Analysts Table */}
        {stats.analysts.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><UserCog className="w-4 h-4" /> Rapport par Analyste ({stats.analysts.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-2">Analyste</th>
                    <th className="text-left py-2 px-2">Bureau</th>
                    <th className="text-right py-2 px-2">Clients</th>
                    <th className="text-right py-2 px-2">AUM</th>
                    <th className="text-right py-2 px-2">P&L</th>
                    <th className="text-right py-2 px-2">Win Rate</th>
                    <th className="text-right py-2 px-2">Placements</th>
                  </tr></thead>
                  <tbody>
                    {stats.analysts.map((a, i) => (
                      <tr key={i} className="border-b border-border/30 hover:bg-secondary/30">
                        <td className="py-2 px-2"><span className="font-semibold">{a.name}</span><br/><span className="text-muted-foreground">{a.email}</span></td>
                        <td className="py-2 px-2">{a.office || '—'}</td>
                        <td className="py-2 px-2 text-right">{a.clients}</td>
                        <td className="py-2 px-2 text-right font-mono">{fmt(a.aum)} €</td>
                        <td className={`py-2 px-2 text-right font-mono ${a.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>{fmt(a.pnl)} €</td>
                        <td className="py-2 px-2 text-right">{a.win_rate}%</td>
                        <td className="py-2 px-2 text-right">{a.subscriptions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 6: Admin Ops Table (Super Admin only) */}
        {isSuperAdmin && stats.admin_ops.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Rapport Admin Ops ({stats.admin_ops.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-2">Admin Ops</th>
                    <th className="text-left py-2 px-2">Email</th>
                    <th className="text-right py-2 px-2">Bureaux</th>
                    <th className="text-right py-2 px-2">Analystes</th>
                    <th className="text-right py-2 px-2">Clients</th>
                  </tr></thead>
                  <tbody>
                    {stats.admin_ops.map((ao, i) => (
                      <tr key={i} className="border-b border-border/30 hover:bg-secondary/30">
                        <td className="py-2 px-2 font-semibold">{ao.name}</td>
                        <td className="py-2 px-2 text-muted-foreground">{ao.email}</td>
                        <td className="py-2 px-2 text-right">{ao.offices}</td>
                        <td className="py-2 px-2 text-right">{ao.analysts}</td>
                        <td className="py-2 px-2 text-right">{ao.clients}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
